import React, { useState, useEffect, useRef } from 'react'
import { Link, navigate } from 'gatsby'
import { Document, Page, pdfjs } from 'react-pdf'
import { useMediaQuery } from 'react-responsive'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import styles from './pdf-viewer.module.css'

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`
}

function BackLink() {
  const [fallbackUrl, setFallbackUrl] = useState('/content-hub')

  useEffect(() => {
    if (window.location.pathname === '/reports/the-infrared-report-2026/') {
      setFallbackUrl('/infrared/100/')
    }
  }, [])

  const handleClick = e => {
    e.preventDefault()
    const hasReferrer = document.referrer
    const cameFromSameSite =
      hasReferrer && new URL(document.referrer).origin === window.location.origin
    if (cameFromSameSite) {
      window.history.back()
    } else {
      navigate(fallbackUrl)
    }
  }

  return (
    <a href={fallbackUrl} onClick={handleClick} className={styles.backButton}>
      <svg version="1.1" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="f1">
            <feDropShadow dx="0" dy="0" stdDeviation="2" floodOpacity="1" />
            <feDropShadow dx="0" dy="0" stdDeviation="1" floodOpacity=".6" />
          </filter>
        </defs>
        <polygon
          fill="#FFFFFF"
          points="34 23 15.707 23 24.3535 14.3535 23.6465 13.6465 13.793 23.5 23.6465 33.3535 24.3535 32.6465 15.707 24 34 24"
          filter="url(#f1)"
        />
      </svg>
    </a>
  )
}

export default function PDFViewer({ url, title, slideImages }) {
  const [numPages, setNumPages] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.clientWidth : 0
  )
  const [windowHeight, setWindowHeight] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.clientHeight : 0
  )
  const [pdfAspectRatio, setPdfAspectRatio] = useState(null)
  const [downloadHovered, setDownloadHovered] = useState(false)
  const [pageInput, setPageInput] = useState('1')
  const pageRefs = useRef([])
  const currentPageRef = useRef(1)
  const containerRef = useRef(null)
  const hoverTimer = useRef(null)


  const isDesktopMQ = useMediaQuery({ query: '(min-width: 821px)' })
  const [isDesktop, setIsDesktop] = useState(null)
  useEffect(() => {
    setIsDesktop(isDesktopMQ)
  }, [isDesktopMQ])

  // Detect actual mobile device (not desktop resized to mobile width)
  const isTouchDevice = useRef(
    typeof window !== 'undefined' && (navigator.maxTouchPoints > 0 || /Mobi|Android/i.test(navigator.userAgent))
  )

  // On real mobile devices, redirect to the native PDF viewer
  useEffect(() => {
    if (isTouchDevice.current && isDesktop === false && url) {
      window.location.href = url
    }
  }, [isDesktop, url])

  const [devicePixelRatio, setDevicePixelRatio] = useState(1)
  useEffect(() => { setDevicePixelRatio(window.devicePixelRatio || 1) }, [])

  // Responsive sizing
  useEffect(() => {
    const update = () => {
      setWindowWidth(document.documentElement.clientWidth)
      setWindowHeight(document.documentElement.clientHeight)
    }
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // windowHeight - 160 leaves ~80px clearance at top (nav) and bottom (download button)
  const mobilePageWidth = windowWidth - 40 // Left-right padding
  const desktopMaxHeight = Math.min(windowHeight * 0.8, windowHeight - 160)
  const desktopMaxWidth = windowWidth - 48 // Left-right padding
  // On desktop: use height unless it would overflow viewport width, then use width
  const desktopPageSize =
    pdfAspectRatio && desktopMaxHeight * pdfAspectRatio > desktopMaxWidth
      ? { width: desktopMaxWidth }
      : { height: desktopMaxHeight }

  const setPage = n => {
    currentPageRef.current = n
    setCurrentPage(n)
    setPageInput(String(n))
  }

  const commitPageInput = () => {
    const n = parseInt(pageInput, 10)
    if (n >= 1 && n <= numPages) {
      goToPage(n)
    } else {
      setPageInput(String(currentPageRef.current))
    }
  }

  // Desktop: scroll-based page tracking on snap container
  useEffect(() => {
    if (!numPages || !isDesktop) return
    const container = containerRef.current
    if (!container) return

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const viewMid = container.clientHeight / 2
        let best = 0
        let bestDist = Infinity
        pageRefs.current.forEach((ref, i) => {
          if (!ref) return
          const rect = ref.getBoundingClientRect()
          const dist = Math.abs(rect.top + rect.height / 2 - viewMid)
          if (dist < bestDist) {
            bestDist = dist
            best = i
          }
        })
        setPage(best + 1)
        ticking = false
      })
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [numPages, isDesktop])

  // Mobile: scroll-based page tracking on window
  useEffect(() => {
    if (!numPages || isDesktop) return

    const getMostVisiblePage = () => {
      let best = 0
      let bestVisible = -1
      pageRefs.current.forEach((ref, i) => {
        if (!ref) return
        const rect = ref.getBoundingClientRect()
        const visiblePx = Math.max(
          0,
          Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
        )
        if (visiblePx > bestVisible) {
          bestVisible = visiblePx
          best = i
        }
      })
      return best + 1
    }

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setPage(getMostVisiblePage())
        ticking = false
      })
    }

    if (window.scrollY === 0) setPage(1)

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [numPages, isDesktop])

  // Arrow key navigation — desktop only
  useEffect(() => {
    if (!isDesktop) return
    const onKey = e => {
      if (e.target.tagName === 'INPUT') return
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        goToPage(Math.min(currentPageRef.current + 1, numPages || 1))
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        goToPage(Math.max(currentPageRef.current - 1, 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [numPages, isDesktop])

  const goToPage = n => {
    setPage(n)
    const container = containerRef.current
    if (container) {
      container.scrollTop = (n - 1) * container.clientHeight
    }
  }

  const onDocumentLoadSuccess = async pdf => {
    setNumPages(pdf.numPages)
    pageRefs.current = new Array(pdf.numPages).fill(null)
    // Detect PDF aspect ratio from first page so desktop sizing can constrain correctly
    try {
      const page = await pdf.getPage(1)
      const viewport = page.getViewport({ scale: 1 })
      setPdfAspectRatio(viewport.width / viewport.height)
    } catch (_) {}
  }

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = `${url}?dl=${encodeURIComponent((title || 'report') + '.pdf')}`
    a.click()
  }

  const downloadButton = (
    <button
      onClick={handleDownload}
      onMouseEnter={() => {
        hoverTimer.current = setTimeout(() => setDownloadHovered(true), 500)
      }}
      onMouseLeave={() => {
        clearTimeout(hoverTimer.current)
        setDownloadHovered(false)
      }}
      className={styles.downloadButton}
    >
      <svg version="1.1" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14,33 L33,33 L33,32 L14,32 L14,33 Z M18.8535,24.1465 L18.1465,24.8535 L23.5,30.207 L28.8535,24.8535 L28.1465,24.1465 L24,28.293 L24,14 L23,14 L23,28.293 L18.8535,24.1465 Z"
          fill="#FFFFFF"
        />
      </svg>
    </button>
  )

  return (
    <>
      {isDesktop && (
        <>
          <BackLink />

          {numPages && (
            <div className={styles.pageNav}>
              <button
                className={styles.pageNavButton}
                onClick={() => goToPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 10L8 6L12 10"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                className={styles.pageCounter}
                id="pdf-page-input"
                name="pdf-page-input"
                type="text"
                value={pageInput}
                onChange={e => setPageInput(e.target.value)}
                onBlur={commitPageInput}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    commitPageInput()
                    e.target.blur()
                  }
                }}
                style={{ width: `${pageInput.length + 2}ch` }}
              />
              <span className={styles.pageCounterTotal}>/ {numPages}</span>
              <button
                className={styles.pageNavButton}
                onClick={() => goToPage(Math.min(currentPage + 1, numPages))}
                disabled={currentPage === numPages}
                title="Next Page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          <div ref={containerRef} className={styles.scrollContainer}>
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className={styles.docLoading}>Loading PDF…</div>}
              error={<div className={styles.docError}>Could not load PDF.</div>}
            >
              {numPages &&
                Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i}
                    ref={el => {
                      pageRefs.current[i] = el
                    }}
                    className={styles.pageWrapper}
                  >
                    <Page
                      pageNumber={i + 1}
                      {...desktopPageSize}
                      renderTextLayer
                      renderAnnotationLayer={false}
                      loading={
                        slideImages[i] ? (
                          <img src={slideImages[i]} style={desktopPageSize} alt="" />
                        ) : (
                          <div className={styles.pagePlaceholder} style={desktopPageSize} />
                        )
                      }
                    />
                  </div>
                ))}
            </Document>
          </div>

          <div className={styles.downloadWrapper}>
            {downloadHovered && (
              <div className={styles.tooltip}>
                Download PDF
                <div className={styles.tooltipArrow} />
              </div>
            )}
            {downloadButton}
          </div>
        </>
      )}

      {isDesktop === false && !isTouchDevice.current && (
        <>
          <div className={styles.mobileTopBar}>
            <Link to='/content-hub' className={styles.mobileBackButton}>
              <svg version='1.1' width='40' height='40' xmlns='http://www.w3.org/2000/svg'>
                <defs>
                  <filter id='f1m'>
                    <feDropShadow dx='0' dy='0' stdDeviation='2' floodOpacity='1' />
                    <feDropShadow dx='0' dy='0' stdDeviation='1' floodOpacity='.6' />
                  </filter>
                </defs>
                <polygon fill='#FFFFFF' points='28 19 13.09 19 20.3 11.79 19.71 11.21 11.41 19.5 19.71 27.79 20.3 27.21 13.09 20 28 20' filter='url(#f1m)' />
              </svg>
            </Link>
            {numPages && (
              <span className={styles.mobilePageCounter}>{currentPage} / {numPages}</span>
            )}
          </div>

          <div className={styles.scrollContainerMobile}>
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className={styles.docLoading}>Loading PDF…</div>}
              error={<div className={styles.docError}>Could not load PDF.</div>}
            >
              {numPages && Array.from({ length: numPages }, (_, i) => (
                <div
                  key={i}
                  ref={el => { pageRefs.current[i] = el }}
                  className={styles.pageWrapperMobile}
                >
                  <Page
                    pageNumber={i + 1}
                    width={mobilePageWidth}
                    devicePixelRatio={devicePixelRatio}
                    renderTextLayer
                    renderAnnotationLayer={false}
                    loading={
                      slideImages[i]
                        ? <img src={slideImages[i]} style={{ width: mobilePageWidth }} alt='' />
                        : <div className={styles.pagePlaceholderMobile} style={{ width: mobilePageWidth }} />
                    }
                  />
                </div>
              ))}
            </Document>
          </div>

          <div className={styles.mobileBottomBar}>
            {downloadHovered && (
              <div className={styles.mobileTooltip}>
                Download PDF
                <div className={styles.tooltipArrow} />
              </div>
            )}
            {downloadButton}
          </div>
        </>
      )}
    </>
  )
}
